import { api, apiRequest } from './client';
import { getUseHardwareHeader } from '@/hooks/useDevHardwareToggle';

export type AmenitySessionState = 'pending' | 'active' | 'completed' | 'failed';

export interface AmenityBlock {
  seconds: number;
  price_brl: string;
}

export interface AmenityDeviceOut {
  id: string;
  station_id: string;
  name: string;
  kind: string;
  is_available: boolean;
  blocks: AmenityBlock[];
  /**
   * Cortesia (1ª aspiração grátis): este cliente ainda tem a dele?
   * Fato do USUÁRIO — vem repetido em cada device porque o endpoint devolve
   * lista. `courtesy_max_seconds` é até onde a cortesia cobre; pacote maior o
   * cliente paga cheio. Ausentes em resposta antiga ⇒ tratar como false.
   */
  courtesy_available?: boolean;
  courtesy_max_seconds?: number | null;
}

export interface StartVacuumIn {
  device_id: string;
  block_seconds?: number | null;
  /**
   * Carteira ('cash' | 'bonus'). Cashback funciona MESMO com saldo negativo
   * (matriz T8d) — é a válvula de escape do cliente em pendência.
   */
  pay_from?: 'cash' | 'bonus';
  /**
   * Superfície que originou a compra — atribuição do funil. Conjunto FECHADO
   * (o backend valida por Literal): o dashboard de aspiradores agrupa por ela.
   */
  origin?: VacuumOrigin;
}

/** Origens válidas de uma compra de aspiração. Espelha o Literal do backend. */
export type VacuumOrigin = 'receipt' | 'push' | 'charging' | 'station' | 'direct';

/** Lê `?origin=` da URL, descartando valor fora do conjunto conhecido. */
export function parseVacuumOrigin(search: string): VacuumOrigin | undefined {
  const raw = new URLSearchParams(search).get('origin');
  const known: readonly string[] = ['receipt', 'push', 'charging', 'station', 'direct'];
  return raw && known.includes(raw) ? (raw as VacuumOrigin) : undefined;
}

export interface AmenitySessionOut {
  id: string;
  device_id: string;
  state: AmenitySessionState;
  price_brl: string;
  block_seconds: number;
  started_at: string;
  expected_end_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
  /** Carteira usada ('cash' | 'bonus'). null = anônimo/legado. */
  pay_from?: 'cash' | 'bonus' | null;
  // Segundos restantes pro cronômetro VISUAL. O corte real é do hardware.
  seconds_remaining: number;
}

/** Estado público do aspirador — página /v/{stationId} (QR na câmera). */
export interface PublicVacuumOut {
  station_id: string;
  station_name: string;
  device_id: string;
  device_name: string;
  is_available: boolean;
  blocks: AmenityBlock[];
}

export interface AnonymousCheckoutOut {
  id: string;
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  price_brl: string;
  block_seconds: number;
  qr_code_text: string;
  qr_code_image_url: string | null;
  expires_at: string;
}

export interface AnonymousCheckoutStatusOut {
  id: string;
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  fulfillment_error: string | null;
  session: AmenitySessionOut | null;
}

export const amenitiesApi = {
  devices: (stationId: string) =>
    api.get<AmenityDeviceOut[]>(`/amenities/devices?station_id=${stationId}`),
  /**
   * Registra que a tela de oferta foi ABERTA — métrica de funil.
   *
   * Fire-and-forget: o backend resolve preço e saldo e grava se o cliente
   * podia pagar. É o que separa "achou caro" de "não tinha saldo" nos ~80%
   * que abrem e saem. Nunca pode atrapalhar a compra — o caller engole o erro.
   */
  trackOfferView: (stationId: string, origin?: VacuumOrigin) =>
    api.post<void>('/amenities/vacuum/offer-viewed', {
      station_id: stationId,
      origin: origin ?? null
    }),
  startVacuum: (body: StartVacuumIn) => {
    // Mesmo padrão do charging: em dev manda X-Bow-Use-Hardware quando o user
    // marca "Hardware real"; backend honra só fora de prod.
    const useHw = getUseHardwareHeader();
    const headers = useHw ? { 'X-Bow-Use-Hardware': useHw } : undefined;
    return apiRequest<AmenitySessionOut>('/amenities/vacuum/start', {
      method: 'POST',
      body,
      headers
    });
  },
  activeSession: () => api.get<AmenitySessionOut | null>('/amenities/sessions/active'),
  getSession: (id: string) => api.get<AmenitySessionOut>(`/amenities/sessions/${id}`),

  // ---- públicos (sem auth) — Pix avulso do QR físico ----
  publicVacuum: (stationId: string) =>
    api.get<PublicVacuumOut>(`/amenities/public/vacuum/${stationId}`),
  createAnonymousCheckout: (
    deviceId: string,
    blockSeconds: number,
    customerName: string,
    customerCpf: string
  ) =>
    api.post<AnonymousCheckoutOut>('/amenities/public/vacuum/checkout', {
      device_id: deviceId,
      block_seconds: blockSeconds,
      // PagBank exige customer na order (validado em sandbox 2026-07-08).
      customer_name: customerName,
      customer_cpf: customerCpf
    }),
  anonymousCheckoutStatus: (chargeId: string) =>
    api.get<AnonymousCheckoutStatusOut>(`/amenities/public/vacuum/checkout/${chargeId}`),
  /** Dev-only (backend 404 em produção) — simula o pagamento do Pix avulso.
   * Chamar só quando `import.meta.env.VITE_SHOW_DEV_TOOLS` estiver ativo. */
  devSimulateAnonymousPayment: (chargeId: string) => {
    const useHw = getUseHardwareHeader();
    const headers = useHw ? { 'X-Bow-Use-Hardware': useHw } : undefined;
    return apiRequest<{ status: string; session_id: string | null }>(
      `/amenities/public/_dev/checkout/${chargeId}/simulate-payment`,
      { method: 'POST', headers }
    );
  }
};
